import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
} from '@mui/material';
import { CheckCircle as CheckIcon, AutoAwesome as AutoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '../../services/api';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const PricingSection = () => {
    // Auto-detect Currency: If timezone is India, default to INR
    const detectDefaultCurrency = () => {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timeZone === 'Asia/Calcutta' || timeZone === 'Asia/Kolkata') {
                return 'inr';
            }
        } catch (e) {
            // Ignore if Intl is not supported
        }
        return 'usd';
    };

    const [currency, setCurrency] = useState(detectDefaultCurrency());
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    const handleCurrencyChange = (event, newCurrency) => {
        if (newCurrency !== null) {
            setCurrency(newCurrency);
        }
    };

    const isINR = currency === 'inr';

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsLoggedIn(!!user);
        });

        const fetchPlans = async () => {
            try {
                // Step 1: Get a temporary backend-issued JWT
                const token = await billingApi.getPublicToken();

                // Step 2: Use the token to fetch sanitized plan config
                const data = await billingApi.getPublicConfig(token);
                if (data && data.plans) {
                    // Convert the object format from server to array format for rendering
                    const plansArray = Object.entries(data.plans).map(([key, value]) => ({
                        ...value,
                        id: key
                    }));
                    setPlans(plansArray);
                }
            } catch (error) {
                console.error("Failed to fetch public plans:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
        return () => unsubscribe();
    }, []);

    const handleSelectPlan = (planId) => {
        if (!isLoggedIn) {
            // Redirect to login with a redirectTo parameter
            navigate(`/app/settings?redirectTo=/app/pricing`);
            // Note: Our RequireAuth inside /app will catch this and show LoginPage
            return;
        }
        navigate('/app/pricing');
    };

    return (
        <Box id="pricing" sx={{ py: { xs: 10, md: 14 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ position: 'absolute', top: '20%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 60%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        PRICING PLANS
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '3.5rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}>
                        Scale your social, not your costs.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', maxWidth: 540, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                        Save over 90% compared to legacy tools like Hootsuite and Sprout Social. Built exclusively on high-efficiency Gemini 1.5 AI.
                    </Typography>

                    {/* Currency Toggle */}
                    <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '100px', p: 0.5 }}>
                        <ToggleButtonGroup
                            value={currency}
                            exclusive
                            onChange={handleCurrencyChange}
                            aria-label="currency pricing"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 4, py: 1, borderRadius: '100px', color: 'rgba(255,255,255,0.5)', fontWeight: 700, border: 'none', textTransform: 'none',
                                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }
                            }}
                        >
                            <ToggleButton value="usd" aria-label="usd pricing">Global (USD)</ToggleButton>
                            <ToggleButton value="inr" aria-label="inr pricing">India (INR)</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <Typography color="rgba(255,255,255,0.4)">Loading plans...</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${plans.length}, 1fr)` }, gap: 3, alignItems: 'stretch' }}>
                        {plans.map((plan, i) => (
                            <Box key={i} sx={{
                                relative: 'relative',
                                display: 'flex', flexDirection: 'column',
                                p: 4, borderRadius: 5,
                                bgcolor: plan.outlined ? 'rgba(255,255,255,0.02)' : `${plan.accent}08`,
                                border: `1px solid ${plan.outlined ? 'rgba(255,255,255,0.08)' : plan.accent}`,
                                boxShadow: plan.outlined ? 'none' : `0 0 40px ${plan.accent}20`,
                                transform: plan.badge ? { md: 'translateY(-16px)' } : 'none',
                                position: 'relative'
                            }}>
                                {plan.badge && (
                                    <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                                        <Chip label={plan.badge} size="small" sx={{ bgcolor: plan.accent, color: '#fff', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.1em' }} />
                                    </Box>
                                )}

                                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>{plan.name}</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>{plan.target}</Typography>

                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 4 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: plan.accent }}>
                                        {isINR ? (plan.price?.inr || 'N/A') : (plan.price?.usd || 'N/A')}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{plan.period}</Typography>
                                </Box>

                                <Stack spacing={2} sx={{ mb: 5, flex: 1 }}>
                                    {(isINR ? (plan.features_ind || []) : (plan.features_global || [])).map((feature, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            {feature.includes('Gemini') ? (
                                                <AutoIcon sx={{ color: plan.accent, fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                            ) : (
                                                <CheckIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                            )}
                                            <Typography variant="body2" sx={{ color: feature.includes('Gemini') ? 'white' : 'rgba(255,255,255,0.7)', fontWeight: feature.includes('Gemini') ? 700 : 500, lineHeight: 1.4 }}>
                                                {feature}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                <Button
                                    onClick={() => handleSelectPlan(plan.id)}
                                    variant={plan.outlined ? "outlined" : "contained"}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '100px',
                                        fontWeight: 800,
                                        fontSize: '1rem',
                                        color: plan.outlined ? 'white' : 'black',
                                        bgcolor: plan.outlined ? 'transparent' : plan.accent,
                                        borderColor: plan.outlined ? 'rgba(255,255,255,0.2)' : 'transparent',
                                        '&:hover': {
                                            bgcolor: plan.outlined ? 'rgba(255,255,255,0.05)' : '#fff',
                                            borderColor: plan.outlined ? 'rgba(255,255,255,0.4)' : 'transparent',
                                        }
                                    }}
                                >
                                    {plan.name === 'Solo Strategist' ? 'Start Free' : `Get ${plan.name}`}
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default PricingSection;
